import { Fragment } from 'react';
import styled from 'styled-components';
import Divider from 'app/ui/components/atoms/divider';

import ResourceSectionDetails from 'app/ui/components/molecules/resource-section-details';
import type { Video } from 'app/types/graphql';
import { ComponentAlignment } from 'app/types/graphql';

interface SectionData {
  title: string;
  content?: string | null;
  images?: Array<{ signedUrl?: string } | null> | null;
  videos?: Array<{ signedUrl?: string } | null> | null;
  documents?: Array<{ signedUrl?: string } | null> | null;
  links?: Array<{ url: string } | null> | null;
  componentAlignment: string;
}

const ResourceSectionDetailsWrapper = styled.div`
  & > div:first-child {
    ${({ theme: { spacing } }) => `margin-top: ${spacing(14)};`};
  }

  ${({ theme: { media } }) =>
    `${media.medium(
      () => `
        & > div:first-child {å
          margin-top: 0;
        }
    `,
    )}`};

  & > div > div {
    margin-top: 0;
  }

  & > div:not(:first-child) {
    ${({ theme: { spacing } }) => `margin-top: ${spacing(24)};`}

    &[data-alignment="horizontal"][data-prev-alignment="horizontal"] {
      ${({ theme: { spacing } }) => `margin-top: ${spacing(32)};`}
    }
  }
`;

type ResourceSectionsProps = {
  sections: SectionData[];
  resourceId: string;
  className?: string;
};

const { VERTICAL } = ComponentAlignment;

const ResourceSections: React.FC<ResourceSectionsProps> = ({ sections, resourceId, className }) => {
  const shouldShowDivider = (index: number) => {
    if (!sections || index === sections?.length - 1) {
      return false;
    }

    const isCurrentVertical = sections[index].componentAlignment === VERTICAL;
    const isNextVertical = sections[index + 1].componentAlignment === VERTICAL;

    return isCurrentVertical && isNextVertical;
  };

  return (
    <ResourceSectionDetailsWrapper className={className}>
      {sections?.map(({ title, content, images, videos, documents, links, componentAlignment }, i: number) => {
        const imageUrl = images?.find((img) => img?.signedUrl)?.signedUrl;
        const video = videos?.find((vid) => vid?.signedUrl);
        const documentUrl = documents?.find((doc) => doc?.signedUrl)?.signedUrl;
        const linkUrl = links?.length ? links[0]?.url : null;

        const prevAlignment = i > 0 ? sections[i - 1].componentAlignment.toLowerCase() : null;
        return (
          <Fragment key={`section-fragment-${title}`}>
            <div data-alignment={componentAlignment.toLowerCase()} data-prev-alignment={prevAlignment}>
              <ResourceSectionDetails
                id={resourceId}
                key={`section-${title}`}
                title={title}
                content={content}
                imageUrl={imageUrl}
                video={video as Video}
                documentUrl={documentUrl || undefined}
                linkUrl={linkUrl || undefined}
                mediaFirst={!!(i % 2)}
                componentAlignment={componentAlignment as ComponentAlignment}
              />
            </div>
            {shouldShowDivider(i) && <Divider key={`section-divider-${title}`} />}
          </Fragment>
        );
      })}
    </ResourceSectionDetailsWrapper>
  );
};

export default ResourceSections;
